import { inject, Injectable } from '@angular/core';
import { ProductPackage } from '@lx/quote-tool/shared/types';
import { SchoolControllerApiService } from '@lx/shared/api';
import { DropOffTypeEnum, Transfer } from '@lx/shared/data-access/products';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader } from '@lx/shared/tokens';
import { getDateStringFromDate, getToday } from '@lx/shared/utils';
import { Memoize } from '@lx/shared/utils-http';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { combineLatest, Observable, of, switchMap, take, tap } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { QuoteToolLiteStore, SharedDataStore } from '../store';
import { Accommodation, ExtrasSummary, HOMESTAY, ReducedAccommodation, RESIDENCE, SchoolExtras } from '../types';
import { OperationKey } from '../constants';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class ExtrasService {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly schoolControllerService = inject(SchoolControllerApiService);
  private readonly loadingService = inject(LoadingService);

  private extrasLoadingStarted = false;

  findSchoolExtras(): Observable<SchoolExtras> {
    return this.quoteToolLiteStore.courseDetailsWithoutCourseAndResidenceCountryAndFlow$.pipe(
      // Mark extras as loading only on first emission
      tap(() => {
        if (!this.extrasLoadingStarted) {
          this.loadingService.startLoading(undefined, OperationKey.extras);
          this.extrasLoadingStarted = true;
        }
      }),
      switchMap(
        ({
          selectedLanguage,
          selectedStartDate,
          selectedEndDate,
          selectedDuration,
          selectedSchoolLocation,
          selectedResidence,
          activeFlow,
          selectedProductPackage,
        }) =>
          combineLatest([
            this.schoolControllerService.findSchoolsExtras(
              selectedSchoolLocation?.schoolCode,
              getDateStringFromDate(getToday()),
              selectedResidence?.countryCode,
              selectedDuration,
              this.locale,
              this.companyHeader,
              selectedProductPackage,
              null,
              selectedLanguage,
              selectedStartDate,
              selectedEndDate,
            ),
            of(activeFlow),
          ]),
      ),
      map(
        ([
          {
            accommodations,
            insurances,
            airportTransfers,
            premiumResidences,
            privateLessons,
            supplements,
            premiumActivities,
            accommodationOptional,
            moreAccommodationsDisclaimerShown,
            professionalImmersionExperience,
          },
          activeFlow,
        ]) => {
          return {
            accommodations,
            accommodationOptional,
            moreAccommodationsDisclaimerShown,
            insurance: insurances?.[0],
            professionalImmersionExperience,
            groupedTransfers: this.getGroupedTransfers(airportTransfers, activeFlow),
            premiumAccommodation: premiumResidences?.[0],
            privateLesson: privateLessons?.[0],
            unaccompaniedMinorServices: supplements?.filter((supplement) => supplement.supplementType === 'UNACCOMPANIEDMINORSERVICE'),
            premiumActivities: premiumActivities,
          };
        },
      ),
      // Mark extras as loaded when we have mapped the response
      tap(() => this.loadingService.stopLoading(OperationKey.extras)),
      finalize(() => this.loadingService.stopLoading(OperationKey.extras)),
    );
  }

  // Public method controls loading state and delegates to the cached implementation.
  findExtrasSummary(flow: Flow, schoolCode: string, startDate: string, productPackage?: ProductPackage): Observable<ExtrasSummary> {
    this.loadingService.setOperationLoading(OperationKey.extrasSummary, true);

    return this.findExtrasSummaryCached(flow, schoolCode, startDate, productPackage).pipe(
      take(1),
      finalize(() => this.loadingService.setOperationLoading(OperationKey.extrasSummary, false)),
    );
  }

  // Cache the result of the extras summary call. Keeping the cached logic in a separate
  // (private) method allows the public `findExtrasSummary` to control loading state.
  @Memoize({
    tags: ['findExtrasSummary'],
    hashFunction: (flow: Flow, schoolCode: string, startDate: string, productPackage?: ProductPackage) =>
      [flow, schoolCode, startDate, productPackage].join('|'),
  })
  private findExtrasSummaryCached(flow: Flow, schoolCode: string, startDate: string, productPackage?: ProductPackage): Observable<ExtrasSummary> {
    const quoteDate = getDateStringFromDate(getToday());
    let extrasSummary$: Observable<ExtrasSummary> | undefined;
    if ((flow as Flow) === Flows.Aya) {
      extrasSummary$ = this.getAyaAccommodationOptions();
    } else {
      extrasSummary$ = this.schoolControllerService.findExtrasSummary(schoolCode, quoteDate, startDate, this.locale, this.companyHeader, productPackage);
    }

    return extrasSummary$.pipe(
      tap((extrasSummary: ExtrasSummary) => {
        if (extrasSummary?.premiumActivities) {
          this.sharedDataStore.updateState({
            suggestedPremiumActivityCode: extrasSummary.premiumActivities.productCode,
          });
        }
      }),
      map((extrasSummary: ExtrasSummary) => {
        const accommodations = [];

        extrasSummary?.accommodations?.forEach((accommodation: ReducedAccommodation) => {
          const builtAccommodation = this.buildAccommodation(accommodation);
          accommodations.push(builtAccommodation);
        });
        accommodations?.sort((accommodation) => (accommodation?.type === HOMESTAY ? -1 : 1));

        return {
          ...extrasSummary,
          accommodations,
          premiumResidences: {
            ...extrasSummary.premiumResidences,
            code: extrasSummary.premiumResidences?.productCode,
          },
          accommodationOptional: (flow as Flow) !== Flows.Aya && extrasSummary?.accommodationOptional,
        };
      }),
    ) as Observable<ExtrasSummary>;
  }

  /**
   * Gets the transfers grouped by transferServiceType and sorted by price (ascending)
   */
  private getGroupedTransfers(transfers: Transfer[], activeFlow: Flow): Record<string, Transfer[]> {
    if (!transfers?.length) {
      return undefined;
    }

    const groupedTransfers = transfers.reduce((groupedTransfers, transfer) => {
      // on Junior flow, only add collective transfers
      if (activeFlow !== Flows.Junior || transfer.dropOffType === DropOffTypeEnum.COLLECTIVE) {
        if (!groupedTransfers[transfer.transferServiceType]) {
          groupedTransfers[transfer.transferServiceType] = [];
        }

        groupedTransfers[transfer.transferServiceType].push(transfer);
      }

      return groupedTransfers;
    }, {} as Record<string, Transfer[]>);

    // Sort the transfers by price (ascending)
    Object.keys(groupedTransfers).forEach((transferServiceType) => {
      groupedTransfers[transferServiceType].sort((firstTransfer, secondTransfer) => firstTransfer.price?.value - secondTransfer.price?.value);
    });

    return groupedTransfers;
  }

  private buildAccommodation({ type, productCode }: ReducedAccommodation) {
    let accommodation = {
      type,
      code: productCode,
    } as Accommodation;

    switch (type) {
      case RESIDENCE:
        accommodation = {
          ...accommodation,
          title: $localize`Residence`,
          image: '/assets/images/residence.png',
          description: $localize`Connect with international students, gain independence, and be part of a social environment!`,
        };
        break;
      case HOMESTAY:
        accommodation = {
          ...accommodation,
          title: $localize`Homestay`,
          image: '/assets/images/homestay.png',
          description: $localize`Integrate with the locals, learn from their culture, and practice your language during daily activities!`,
        };
        break;
    }
    return accommodation;
  }

  /**
   * Gets accommodation options for AYA flow (Homestay and Residence only)
   */
  private getAyaAccommodationOptions(): Observable<ExtrasSummary> {
    return of({
      accommodations: [
        { type: HOMESTAY, productCode: HOMESTAY } as ReducedAccommodation,
        {
          type: RESIDENCE,
          productCode: RESIDENCE,
        } as ReducedAccommodation,
      ],
    } as ExtrasSummary);
  }
}
